import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { SkuProductAttributeInput } from "__generated__/gql/graphql";
import { makeClearNestedListFieldCache } from "utils/apollo/cacheHelpers";
import { addActiveBrandIdToContext } from "utils/apollo/hooks/brand";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { chunkArrayByByteSize, DEFAULT_MAX_BYTE_SIZE } from "utils/misc";
import { formatInteger } from "utils/formatting";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import AttributeIsCertificationIcon from "components/Shared/AttributeIsCertificationIcon";
import { useCurrentBrandId } from "utils/currentUserHooks";
import UserError from "utils/UserError";
import { BulkSelectionStatus, getBulkSelectionStatus } from "./utils";

const attributeFragment = gql(/* GraphQL */ `
  fragment ProductAttributesMatrixBulkModalAttribute on Attribute {
    id
    name
    isCertification
  }
`);

const skuFragment = gql(/* GraphQL */ `
  fragment ProductAttributesMatrixBulkModalSku on Sku {
    id
    name
  }
`);

const assignMutation = gql(/* GraphQL */ `
  mutation ProductAttributesMatrixBulkEditModalUpsertSkuProductAttributes(
    $skuProductAttributes: [SkuProductAttributeInput!]!
  ) {
    upsertSkuProductAttributesBySkuIdAndProductAttributeId(
      input: { skuProductAttributes: $skuProductAttributes }
    ) {
      clientMutationId
    }
  }
`);

const unassignMutation = gql(/* GraphQL */ `
  mutation ProductAttributesMatrixBulkEditModalDeleteSkuProductAttributes(
    $skuProductAttributes: [DeleteSkuProductAttributesBySkuIdAndProductAttributeIdItemInput!]!
  ) {
    deleteSkuProductAttributesBySkuIdAndProductAttributeId(
      input: { skuProductAttributes: $skuProductAttributes }
    ) {
      clientMutationId
    }
  }
`);

function getStatusCheckedValue(status: BulkSelectionStatus) {
  if (status === BulkSelectionStatus.All) return true;
  if (status === BulkSelectionStatus.None) return false;
  return undefined;
}

function getStatusBadgeText(status: BulkSelectionStatus) {
  if (status === BulkSelectionStatus.All) return "All";
  if (status === BulkSelectionStatus.None) return "None";
  return "Some";
}

type Props = {
  attributes: FragmentType<typeof attributeFragment>[];
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedSkuAttributeIdsBySkuId: Readonly<Record<string, readonly UUID[]>>;
  selectedSkus: FragmentType<typeof skuFragment>[];
};

export default function ProductAttributesBulkEditModal({
  attributes,
  isOpen,
  onClose,
  onComplete,
  selectedSkuAttributeIdsBySkuId,
  selectedSkus,
}: Props) {
  const client = useApolloClient();
  const brandId = useCurrentBrandId();
  const { showErrorToast, showSuccessToast } = useAsyncCompleteToast();
  const preparedAttributes = usePrepApolloData(
    getFragmentData(attributeFragment, attributes),
  );
  const preparedSkus = usePrepApolloData(
    getFragmentData(skuFragment, selectedSkus),
  );

  const [changesByAttributeId, setChangesByAttributeId] = useState<
    Record<string, boolean>
  >({});
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChangesByAttributeId({});
      setIsApplying(false);
    }
  }, [isOpen]);

  const selectedSkuAttributeIds = useMemo(
    () =>
      preparedSkus.map((sku) => selectedSkuAttributeIdsBySkuId[sku.id] || []),
    [preparedSkus, selectedSkuAttributeIdsBySkuId],
  );

  const statusByAttributeId = useMemo(
    () =>
      preparedAttributes.reduce<Record<string, BulkSelectionStatus>>(
        (acc, attribute) => {
          acc[attribute.id] = getBulkSelectionStatus(
            attribute.id,
            selectedSkuAttributeIds,
          );
          return acc;
        },
        {},
      ),
    [preparedAttributes, selectedSkuAttributeIds],
  );

  const numPendingChanges = Object.keys(changesByAttributeId).length;

  const handleApply = async () => {
    if (numPendingChanges === 0 || preparedSkus.length === 0) {
      throw new UserError("No changes to apply");
    }

    const attributesToAssign: SkuProductAttributeInput[] = [];
    const attributesToUnassign: SkuProductAttributeInput[] = [];

    preparedSkus.forEach((sku) => {
      const skuId = sku.id;
      const currentAttributeIds = new Set(
        selectedSkuAttributeIdsBySkuId[skuId],
      );

      Object.entries(changesByAttributeId).forEach(
        ([attributeId, shouldAssign]) => {
          const productAttributeId = attributeId;
          const isAssigned = currentAttributeIds.has(productAttributeId);

          if (shouldAssign && !isAssigned) {
            attributesToAssign.push({ skuId, productAttributeId });
          }

          if (!shouldAssign && isAssigned) {
            attributesToUnassign.push({ skuId, productAttributeId });
          }
        },
      );
    });

    setIsApplying(true);

    async function applyMutation<T extends SkuProductAttributeInput>({
      attributes: attributesToMutate,
      initialNumFailed = 0,
      mutation,
    }: {
      attributes: T[];
      initialNumFailed?: number;
      mutation: typeof assignMutation | typeof unassignMutation;
    }) {
      const [chunks] = chunkArrayByByteSize(attributesToMutate, {
        maxByteSize: DEFAULT_MAX_BYTE_SIZE - 1500,
      });

      return chunks.reduce(async (numFailedPromise, chunk) => {
        const numFailed = await numFailedPromise;

        try {
          await client.mutate({
            fetchPolicy: "no-cache",
            mutation,
            variables: { skuProductAttributes: chunk },
            context: addActiveBrandIdToContext(brandId),
          });
          return numFailed;
        } catch {
          return numFailed + 1;
        }
      }, Promise.resolve(initialNumFailed));
    }

    const numFailedAfterAssigns = await applyMutation({
      attributes: attributesToAssign,
      mutation: assignMutation,
    });

    const numFailed = await applyMutation({
      attributes: attributesToUnassign,
      mutation: unassignMutation,
      initialNumFailed: numFailedAfterAssigns,
    });

    setIsApplying(false);

    if (numFailed > 0) {
      throw new UserError("Some SKU updates failed", {
        description: `${formatInteger(numFailed)} SKU ${
          numFailed === 1 ? "" : "s"
        } failed to apply the changes.`,
      });
    }

    const numApplied = attributesToAssign.length + attributesToUnassign.length;
    if (numApplied > 0) {
      const clearNestedListFieldCache = makeClearNestedListFieldCache(
        client.cache,
      );
      clearNestedListFieldCache("Sku", ["productAttributes"], {
        broadcast: true,
      });
      client.cache.gc();

      showSuccessToast(
        `Updated ${formatInteger(numApplied)} SKU attribute assignment${
          numApplied === 1 ? "" : "s"
        }`,
      );
    }

    onComplete();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Multiple SKUs</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing={4}>
            <Text color="gray.600" fontSize="sm">
              Apply product attribute assignments to{" "}
              {formatInteger(preparedSkus.length)} selected SKUs.
            </Text>

            <Box
              borderWidth={1}
              borderRadius="md"
              maxH="28rem"
              overflowY="auto"
              p={3}
            >
              <Stack spacing={3}>
                {preparedAttributes.map((attribute) => {
                  const status =
                    statusByAttributeId[attribute.id] ||
                    BulkSelectionStatus.None;
                  const isPendingChecked = changesByAttributeId[attribute.id];

                  const isStatusChecked = getStatusCheckedValue(status);

                  const isChecked =
                    typeof isPendingChecked === "boolean"
                      ? isPendingChecked
                      : status === BulkSelectionStatus.All;

                  const isIndeterminate =
                    typeof isPendingChecked !== "boolean" &&
                    status === BulkSelectionStatus.Some;

                  return (
                    <Flex key={attribute.id} alignItems="center" gap={3}>
                      <Checkbox
                        isChecked={isChecked}
                        isIndeterminate={isIndeterminate}
                        onChange={(evt) => {
                          const isCheckedNext = evt.target.checked;

                          setChangesByAttributeId((existing) => {
                            if (isStatusChecked === isCheckedNext) {
                              const withoutCurrent = { ...existing };
                              delete withoutCurrent[attribute.id];
                              return withoutCurrent;
                            }

                            return {
                              ...existing,
                              [attribute.id]: isCheckedNext,
                            };
                          });
                        }}
                      >
                        {attribute.name}
                        {attribute.isCertification ? (
                          <AttributeIsCertificationIcon color="gray.600" />
                        ) : null}
                      </Checkbox>

                      <Badge ml="auto">{getStatusBadgeText(status)}</Badge>
                    </Flex>
                  );
                })}

                {preparedAttributes.length === 0 && (
                  <Text color="gray.500" fontSize="sm">
                    No active product attributes are available.
                  </Text>
                )}
              </Stack>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} variant="ghost" onClick={onClose} size="sm">
            Cancel
          </Button>

          <Button
            colorScheme="pinkButton"
            size="sm"
            isLoading={isApplying}
            isDisabled={numPendingChanges === 0 || preparedSkus.length === 0}
            onClick={() => {
              handleApply().catch(showErrorToast);
            }}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
